import { RootState } from '@/redux/store/configureStore'
import { InterstitialData, SrpFareObject, SubTravelOptions } from '@/types/srp'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * Keyed store for `/flight/fetch-interstitial-page` responses.
 *
 * Each response lands in its own slot, keyed by a hash of the request that
 * produced it (see `buildInterstitialCacheKey`). Nothing is ever spread into a
 * map shared with another response, which is the whole point: the previous
 * design merged every response into `searchData.subTravelOptions`, so a
 * special-RT payload — which describes the trip as one combined fare and
 * carries no per-leg fare families — would overwrite the per-leg entries a
 * regular-RT response had just written, while the "already fetched" flag for
 * that leg survived and claimed data that no longer existed.
 *
 * `srpSlice.mergeInterstitialData` still runs alongside this, because the SRP
 * tuples, coupons, WOW pricing and itinerary-create paths all read the merged
 * maps. This slice is what the fare-family modal renders from.
 */

/** Beyond this, an entry still paints but is refreshed in the background. */
export const INTERSTITIAL_STALE_AFTER_MS = 10 * 60 * 1000

export type InterstitialEntryStatus = 'loading' | 'success' | 'dialogue' | 'error'

export interface InterstitialEntry {
    status: InterstitialEntryStatus
    /** Search session this belongs to; a new search makes it unusable. */
    dataId: string
    fetchedAt: number
    fares: SrpFareObject
    subTravelOptions: SubTravelOptions
    /** Special-RT combined fares, keyed `[onwardTravelOptionId, returnTravelOptionId]`. */
    combinedFareIds: Record<string, string[]>
    benefits: Record<string, any>
    baggageAllowances: Record<string, any>
    couponUIResponse?: any
    userDialogue?: any
    error?: string
}

interface InterstitialCacheState {
    entries: Record<string, InterstitialEntry>
}

const initialState: InterstitialCacheState = { entries: {} }

export type FetchInterstitialArgs = {
    key: string
    dataId: string
    payload: Record<string, any>
    isCfw?: boolean
}

/**
 * Owns the interstitial network call, cache writes, and in-flight dedup.
 * Dedup uses the entry's `loading` status (set synchronously on `pending`) so
 * a React 18 StrictMode double-invoke cannot start two requests for the same
 * key — no module-level / globalThis promise map needed.
 */
export const fetchInterstitialData = createAsyncThunk(
    'interstitialCache/fetch',
    async ({ payload, isCfw = false }: FetchInterstitialArgs) => {
        // Lazy import keeps this slice free of the api/srp → network → utils
        // cycle that otherwise blows up at module load in Jest.
        const { getSrpFareData } = await import('@/api/srp')
        return getSrpFareData(payload, isCfw)
    },
    {
        condition: ({ key }, { getState }) => {
            if (!key) return false
            const state = getState() as RootState
            const entry = state.interstitialCache?.entries?.[key]
            return entry?.status !== 'loading'
        }
    }
)

const emptyEntry = (dataId: string, status: InterstitialEntryStatus): InterstitialEntry => ({
    status,
    dataId,
    fetchedAt: 0,
    fares: {},
    subTravelOptions: {},
    combinedFareIds: {},
    benefits: {},
    baggageAllowances: {}
})

const applyRequested = (state: InterstitialCacheState, key: string, dataId: string) => {
    if (!key) return
    const existing = state.entries[key]
    if (existing) {
        existing.status = 'loading'
        return
    }
    state.entries[key] = emptyEntry(dataId, 'loading')
}

const applySucceeded = (
    state: InterstitialCacheState,
    key: string,
    dataId: string,
    response: Partial<InterstitialData>
) => {
    if (!key) return
    const userDialogue = (response as any)?.userDialogue
    state.entries[key] = {
        status: userDialogue ? 'dialogue' : 'success',
        dataId,
        fetchedAt: Date.now(),
        fares: response?.fares || {},
        subTravelOptions: response?.subTravelOptions || {},
        combinedFareIds: (response as any)?.travelOptionIdsToFareIdsMap || {},
        benefits: response?.benefits || {},
        baggageAllowances: response?.baggageAllowances || {},
        couponUIResponse: (response as any)?.couponUIResponse,
        userDialogue
    }
}

const applyFailed = (state: InterstitialCacheState, key: string, dataId: string, error?: string) => {
    if (!key) return
    const existing = state.entries[key]
    const hasData =
        !!existing &&
        (Object.keys(existing.subTravelOptions).length > 0 || Object.keys(existing.combinedFareIds).length > 0)
    if (existing && hasData) {
        existing.status = existing.userDialogue ? 'dialogue' : 'success'
        existing.error = error
        return
    }
    state.entries[key] = { ...emptyEntry(dataId, 'error'), error, fetchedAt: Date.now() }
}

export const slice = createSlice({
    name: 'interstitialCache',
    initialState,
    reducers: {
        /** Marks a fetch in flight. Never clears an existing entry — a refresh
         *  must keep painting the data it is replacing. */
        interstitialRequested(state, action: PayloadAction<{ key: string; dataId: string }>) {
            const { key, dataId } = action.payload
            applyRequested(state, key, dataId)
        },

        interstitialSucceeded(
            state,
            action: PayloadAction<{ key: string; dataId: string; response: Partial<InterstitialData> }>
        ) {
            const { key, dataId, response } = action.payload
            applySucceeded(state, key, dataId, response)
        },

        /** A failed refresh must not destroy a good entry — it degrades to the
         *  data we already have, which is what keeps a stale-but-valid modal
         *  rendering instead of collapsing to an empty fare strip. */
        interstitialFailed(state, action: PayloadAction<{ key: string; dataId: string; error?: string }>) {
            const { key, dataId, error } = action.payload
            // Keyed on whether the entry holds anything renderable, not on its
            // status: a refresh sets the status to 'loading' first, so checking
            // the status here would throw away the very data this is meant to
            // protect. Special-RT entries carry combined ids and no per-leg
            // subTravelOptions, so both count as data.
            applyFailed(state, key, dataId, error)
        },

        /** Drops everything belonging to a superseded search. */
        pruneInterstitialCache(state, action: PayloadAction<string>) {
            const dataId = action.payload
            for (const key of Object.keys(state.entries)) {
                if (state.entries[key].dataId !== dataId) delete state.entries[key]
            }
        },

        clearInterstitialCache(state) {
            state.entries = {}
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchInterstitialData.pending, (state, action) => {
                const { key, dataId } = action.meta.arg
                applyRequested(state, key, dataId)
            })
            .addCase(fetchInterstitialData.fulfilled, (state, action) => {
                const { key, dataId } = action.meta.arg
                applySucceeded(state, key, dataId, (action.payload || {}) as Partial<InterstitialData>)
            })
            .addCase(fetchInterstitialData.rejected, (state, action) => {
                // Condition abort is not a failure — another call for this key is already running.
                if (action.meta.condition) return
                const { key, dataId } = action.meta.arg
                applyFailed(
                    state,
                    key,
                    dataId,
                    action.error?.message || 'interstitial fetch failed'
                )
            })
        /**
         * A new search invalidates every cached interstitial. Matched by action
         * type string rather than by importing the action creator, because
         * srpSlice pulls in the SRP helpers and this module already depends on
         * RootState — importing across the two would close an import cycle
         * through `configureStore -> reducers -> srpSlice`.
         */
        builder.addMatcher(
            action => action.type === 'srpSeachData/setSrpSearchData' || action.type === 'srpSeachData/flushData',
            state => {
                state.entries = {}
            }
        )
    }
})

export const {
    interstitialRequested,
    interstitialSucceeded,
    interstitialFailed,
    pruneInterstitialCache,
    clearInterstitialCache
} = slice.actions

/* --------------------------------- selectors -------------------------------- */

export const getInterstitialEntries = (state: RootState) => state.interstitialCache.entries

/**
 * An entry is only usable for the search that produced it. Returning
 * `undefined` for a superseded `dataId` means a stale entry can never be read,
 * even if pruning has not run yet.
 */
export const selectInterstitialEntry = (state: RootState, key: string): InterstitialEntry | undefined => {
    if (!key) return undefined
    const entry = state.interstitialCache.entries[key]
    if (!entry) return undefined
    const currentDataId = state.srpSeachData?.searchData?.dataId || ''
    if (currentDataId && entry.dataId && entry.dataId !== currentDataId) return undefined
    return entry
}

export const isInterstitialEntryStale = (entry?: InterstitialEntry): boolean => {
    if (!entry || !entry.fetchedAt) return false
    return Date.now() - entry.fetchedAt > INTERSTITIAL_STALE_AFTER_MS
}

export default slice.reducer
